import { useEffect, useRef, useState } from 'react'
import { Terminal } from 'xterm'
import { FitAddon } from 'xterm-addon-fit'
import 'xterm/css/xterm.css'
import { getToken } from '../lib/api'

// VMConsole is a live in-browser terminal to a VM's serial console. It opens a
// WebSocket to the control plane's console endpoint (/api/v1/vms/:id/console),
// which the backend bridges to the guest's /dev/ttyS0 (via the L1 ssh-bridge).
// Bytes are relayed raw in both directions: keystrokes go up as BINARY frames,
// guest output comes down as BINARY frames and is written straight to xterm.
//
// Auth: WebSockets can't set an Authorization header from the browser, so the
// JWT is passed as ?token= (the auth middleware accepts it for WS/console URLs).
export default function VMConsole({ id }: { id: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed' | 'error'>('connecting')

  useEffect(() => {
    if (!containerRef.current) return

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 13,
      theme: { background: '#0b0f19', foreground: '#e5e7eb' },
      convertEol: true,
    })
    const fit = new FitAddon()
    term.loadAddon(fit)
    term.open(containerRef.current)
    try {
      fit.fit()
    } catch {
      /* container not sized yet — a later resize will fit */
    }

    // Build the console WS URL against the current origin (nginx proxies it to the
    // control plane). token goes in the query (WS can't carry an auth header).
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const token = getToken() ?? ''
    const url = `${proto}://${window.location.host}/api/v1/vms/${encodeURIComponent(id)}/console?token=${encodeURIComponent(token)}`
    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      setStatus('open')
      // Nudge the shell to paint a fresh prompt.
      ws.send(new TextEncoder().encode('\r'))
      term.focus()
    }
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        // Backend error frames ({type,message}) arrive as text; show them inline.
        try {
          const j = JSON.parse(ev.data)
          if (j && j.message) term.write(`\r\n\x1b[33m[${j.type || 'info'}] ${j.message}\x1b[0m\r\n`)
          else term.write(ev.data)
        } catch {
          term.write(ev.data)
        }
        return
      }
      term.write(new Uint8Array(ev.data as ArrayBuffer))
    }
    ws.onerror = () => setStatus('error')
    ws.onclose = () => setStatus('closed')

    const onData = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(new TextEncoder().encode(data))
      }
    })

    const onResize = () => {
      try {
        fit.fit()
      } catch {
        /* ignore */
      }
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      onData.dispose()
      ws.close()
      term.dispose()
    }
  }, [id])

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-xs">
        <span
          className={
            'inline-block h-2 w-2 rounded-full ' +
            (status === 'open'
              ? 'bg-green-500'
              : status === 'connecting'
                ? 'bg-amber-400'
                : 'bg-red-500')
          }
        />
        <span className="text-gray-500">
          {status === 'open'
            ? 'Connected — this is a live root shell'
            : status === 'connecting'
              ? 'Connecting to console…'
              : status === 'error'
                ? 'Console connection error'
                : 'Console disconnected'}
        </span>
      </div>
      <div
        ref={containerRef}
        className="rounded-lg border border-gray-800 bg-[#0b0f19] p-2"
        style={{ height: 360 }}
      />
    </div>
  )
}
